/**
 * Canvas Token Map
 *
 * Single source of truth for canvas-facing colors.
 * Maps design system tokens to hex values for Fabric.js compatibility.
 *
 * Usage:
 * - Import CANVAS_COLORS into canvas modules
 * - Use semantic names instead of raw hex
 * - Centralizes color changes for canvas rendering
 */

export const CANVAS_COLORS = {
  // Text colors
  text: {
    default: '#3D3B36', // primary-400 (default text)
    secondary: '#737373', // ink-500 (muted text)
    heading: '#3D3B36', // primary-400 (charcoal headings)
    light: '#FFFFFF', // For dark backgrounds
  },

  // Primary brand colors
  primary: {
    main: '#3D3B36', // primary-400
    dark: '#2A2823', // primary-500
    light: '#8C8983', // primary-200
  },

  // Secondary colors
  secondary: {
    main: '#7BB3A5', // secondary-400
    dark: '#5C8A7E', // secondary-500
    light: '#A3CCC1', // secondary-300
  },

  // Neutral/ink colors
  neutral: {
    50: '#FAFAF9',
    100: '#F5F5F4',
    200: '#E7E5E4',
    300: '#D6D3D1',
    400: '#A8A29E',
    500: '#78716C',
    600: '#57534E',
    700: '#44403C',
    800: '#292524',
    900: '#1C1917',
  },

  ink: {
    50: '#FAFAF9',
    100: '#F4F4F3',
    200: '#E8E7E5',
    300: '#D3D2CF',
    400: '#A19F9C',
    500: '#6B6660',
    600: '#534F4B',
    700: '#3D3B36',
    800: '#2A2823',
    900: '#1A1815',
  },

  // Semantic colors
  success: '#16A34A', // green-600
  warning: '#CA8A04', // yellow-600
  error: '#DC2626', // red-600
  info: '#0891B2', // cyan-600

  // Canvas-specific
  selection: {
    border: '#7BB3A5', // secondary-400 (selection border)
    corner: '#7BB3A5', // secondary-400 (corner controls)
    warningBorder: '#FCA5A5', // red-300 (quality warning)
    warningCorner: '#FCA5A5', // red-300 (quality warning)
  },

  zone: {
    stroke: '#E7E5E4', // neutral-200 (zone borders)
    label: '#78716C', // neutral-500 (zone labels)
  },

  background: {
    board: '#FDFCF9', // parchment mist, default board canvas surface
    white: '#FFFFFF',
    canvas: '#FAFAF9', // neutral-50
  },
} as const

/**
 * Get a color value from the token map
 * Throws if token doesn't exist to catch typos early
 */
export function getCanvasColor(path: string): string {
  const parts = path.split('.')
  let value: unknown = CANVAS_COLORS

  for (const part of parts) {
    if (
      value === null ||
      typeof value !== 'object' ||
      !(part in (value as Record<string, unknown>))
    ) {
      throw new Error(`Canvas color token not found: ${path}`)
    }
    value = (value as Record<string, unknown>)[part]
  }

  if (typeof value !== 'string') {
    throw new Error(`Canvas color token is not a color value: ${path}`)
  }

  return value
}
